import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Tooltip,
  Chip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
} from '@mui/icons-material'
import { riskCodesAPI, domainsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const VALUE_FORMATS = [
  { value: 'presence', label: 'Presence / Not Present', description: 'No value; just present or not.' },
  { value: 'discrete', label: 'Discrete Values', description: 'A fixed list of allowed string values.' },
  { value: 'numeric', label: 'Numeric Range', description: 'A number within a min / max range.' },
]

function formatSummary(risk) {
  if (!risk) return null
  if (risk.value_format === 'presence') return 'Presence'
  if (risk.value_format === 'discrete') {
    const vals = Array.isArray(risk.discrete_values) ? risk.discrete_values : []
    return `Discrete (${vals.length}): ${vals.join(', ')}`
  }
  if (risk.value_format === 'numeric') {
    return `Numeric [${risk.numeric_min}, ${risk.numeric_max}]`
  }
  return risk.value_format || '—'
}

const emptyForm = {
  code: '',
  name: '',
  description: '',
  category: '',
  value_format: 'discrete',
  discrete_values: ['low', 'medium', 'high', 'critical'],
  numeric_min: '',
  numeric_max: '',
}

function RiskCodes() {
  const { canManageBaseContent } = useAuth()
  const [riskCodes, setRiskCodes] = useState([])
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRisk, setEditingRisk] = useState(null)
  const [selectedRisk, setSelectedRisk] = useState(null)
  const [formData, setFormData] = useState(emptyForm)
  const [discreteInput, setDiscreteInput] = useState('')
  const [saving, setSaving] = useState(false)

  // Resolve a domain code to its human-readable name (or the code itself).
  const domainLabel = (code) => domains.find((d) => d.code === code)?.name || code

  useEffect(() => {
    fetchRiskCodes()
  }, [])

  const fetchRiskCodes = async () => {
    try {
      setLoading(true)
      setError(null)
      const [rcRes, domainsRes] = await Promise.all([
        riskCodesAPI.getAll(),
        domainsAPI.getAll().catch(() => ({ data: [] })),
      ])
      setRiskCodes(rcRes.data || [])
      setDomains(domainsRes.data || [])
    } catch (err) {
      console.error('Error fetching risk codes:', err)
      setError('Failed to load risk codes. The API endpoint may not be configured yet.')
      setRiskCodes([])
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (risk = null) => {
    if (risk) {
      setEditingRisk(risk)
      setFormData({
        code: risk.code || '',
        name: risk.name || '',
        description: risk.description || '',
        category: risk.category || '',
        value_format: risk.value_format || 'discrete',
        discrete_values: Array.isArray(risk.discrete_values) ? [...risk.discrete_values] : [],
        numeric_min: risk.numeric_min != null ? String(risk.numeric_min) : '',
        numeric_max: risk.numeric_max != null ? String(risk.numeric_max) : '',
      })
    } else {
      setEditingRisk(null)
      setFormData(emptyForm)
    }
    setDiscreteInput('')
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingRisk(null)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      // Build the payload based on selected format — only send format-relevant fields
      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        category: formData.category,
        value_format: formData.value_format,
        discrete_values: formData.value_format === 'discrete' ? formData.discrete_values : null,
        numeric_min: formData.value_format === 'numeric' ? Number(formData.numeric_min) : null,
        numeric_max: formData.value_format === 'numeric' ? Number(formData.numeric_max) : null,
      }

      // Client-side validation
      if (payload.value_format === 'discrete') {
        if (!Array.isArray(payload.discrete_values) || payload.discrete_values.length === 0) {
          setError('Discrete format requires at least one allowed value.')
          setSaving(false)
          return
        }
      }
      if (payload.value_format === 'numeric') {
        if (!Number.isFinite(payload.numeric_min) || !Number.isFinite(payload.numeric_max)) {
          setError('Numeric format requires both min and max as numbers.')
          setSaving(false)
          return
        }
        if (!(payload.numeric_min < payload.numeric_max)) {
          setError('Numeric min must be strictly less than max.')
          setSaving(false)
          return
        }
      }

      if (editingRisk) {
        await riskCodesAPI.update(editingRisk.id, payload)
      } else {
        await riskCodesAPI.create(payload)
      }
      handleCloseDialog()
      fetchRiskCodes()
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save risk code.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this risk code?')) {
      return
    }
    try {
      await riskCodesAPI.delete(id)
      if (selectedRisk?.id === id) setSelectedRisk(null)
      fetchRiskCodes()
    } catch (err) {
      setError('Failed to delete risk code.')
    }
  }

  const handleDuplicate = async (risk) => {
    try {
      setError(null)
      await riskCodesAPI.duplicate(risk.id, {
        code: `${risk.code}_COPY`,
        name: `${risk.name} (Copy)`,
      })
      fetchRiskCodes()
    } catch (err) {
      console.error('Error duplicating risk code:', err)
      setError(err.response?.data?.message || 'Failed to duplicate risk code.')
    }
  }

  const canEditRisk = (risk) => {
    if (risk.is_base && !canManageBaseContent('risk_codes')) return false
    return true
  }

  const canDeleteRisk = (risk) => {
    if (risk.is_base && !canManageBaseContent('risk_codes')) return false
    return true
  }

  const addDiscreteValue = () => {
    const v = discreteInput.trim()
    if (!v) return
    if (formData.discrete_values.includes(v)) {
      setError('Duplicate value — each entry must be unique.')
      return
    }
    setFormData(prev => ({ ...prev, discrete_values: [...prev.discrete_values, v] }))
    setDiscreteInput('')
  }

  const removeDiscreteValue = (v) => {
    setFormData(prev => ({
      ...prev,
      discrete_values: prev.discrete_values.filter(x => x !== v),
    }))
  }

  // Group risk codes by their domain (stored in the `category` column).
  const risksByCategory = riskCodes.reduce((acc, risk) => {
    const category = risk.category || 'Unassigned'
    if (!acc[category]) acc[category] = []
    acc[category].push(risk)
    return acc
  }, {})

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Risk Codes</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage risk codes that can be assigned to patients. Each risk code
            defines a value format (presence, discrete list, or numeric range);
            rules that reference it must match the format.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Risk Code
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Risk Code List */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
              Risk Codes
            </Typography>
            {Object.keys(risksByCategory).length === 0 ? (
              <Alert severity="info">
                No risk codes defined yet. Create your first risk code to get started.
              </Alert>
            ) : (
              Object.entries(risksByCategory).map(([category, risks]) => (
                <Box key={category} mb={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {category === 'Unassigned' ? 'Unassigned' : domainLabel(category)}
                  </Typography>
                  <List dense>
                    {risks.map((risk) => (
                      <ListItem
                        key={risk.id}
                        button
                        selected={selectedRisk?.id === risk.id}
                        onClick={() => setSelectedRisk(risk)}
                        secondaryAction={
                          <Box display="flex" gap={0.25}>
                            <Tooltip title={canEditRisk(risk) ? 'Edit' : 'View'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleOpenDialog(risk)
                                }}
                              >
                                {canEditRisk(risk) ? (
                                  <EditIcon fontSize="small" />
                                ) : (
                                  <ViewIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDuplicate(risk)
                                }}
                              >
                                <DuplicateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canDeleteRisk(risk) && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDelete(risk.id)
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">{risk.name}</Typography>
                              {risk.is_base && (
                                <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
                                  Base
                                </Typography>
                              )}
                              <Chip
                                label={risk.value_format || 'discrete'}
                                size="small"
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.625rem', textTransform: 'capitalize' }}
                              />
                            </Box>
                          }
                          secondary={risk.code}
                          secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.6875rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider />
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Risk Code Details */}
        <Grid item xs={12} md={7}>
          {selectedRisk ? (
            <Paper sx={{ p: 2 }}>
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {selectedRisk.name}
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.6875rem" color="text.secondary">
                  {selectedRisk.code}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedRisk.description || 'No description provided.'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Domain
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedRisk.category ? domainLabel(selectedRisk.category) : 'Unassigned'}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Value Format
              </Typography>
              <Box mt={0.5} mb={2}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {selectedRisk.value_format || '—'}
                </Typography>
                {selectedRisk.value_format === 'discrete' && (
                  <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                    {(selectedRisk.discrete_values || []).map(v => (
                      <Chip key={v} label={v} size="small" sx={{ fontSize: '0.75rem' }} />
                    ))}
                  </Box>
                )}
                {selectedRisk.value_format === 'numeric' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Range: {selectedRisk.numeric_min} – {selectedRisk.numeric_max}
                  </Typography>
                )}
              </Box>

            </Paper>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  Select a risk code to view details
                </Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRisk ? 'Edit Risk Code' : 'Create Risk Code'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })
                }
                required
                disabled={!!editingRisk}
                helperText="e.g., RISK_HIGH_ANXIETY"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            {/* Value Format */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Value Format
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.value_format}
                  onChange={(e) => setFormData({ ...formData, value_format: e.target.value })}
                >
                  {VALUE_FORMATS.map(f => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {VALUE_FORMATS.find(f => f.value === formData.value_format)?.description}
              </Typography>
            </Grid>

            {/* Discrete values */}
            {formData.value_format === 'discrete' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Allowed Values
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a value and press Enter"
                    value={discreteInput}
                    onChange={(e) => setDiscreteInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addDiscreteValue()
                      }
                    }}
                  />
                  <Button variant="outlined" onClick={addDiscreteValue} disabled={!discreteInput.trim()}>
                    Add
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {formData.discrete_values.map(v => (
                    <Chip
                      key={v}
                      label={v}
                      onDelete={() => removeDiscreteValue(v)}
                      deleteIcon={<ClearIcon />}
                      size="small"
                    />
                  ))}
                  {formData.discrete_values.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No values yet — add at least one.
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Numeric range */}
            {formData.value_format === 'numeric' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min"
                    type="number"
                    value={formData.numeric_min}
                    onChange={(e) => setFormData({ ...formData, numeric_min: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Max"
                    type="number"
                    value={formData.numeric_max}
                    onChange={(e) => setFormData({ ...formData, numeric_max: e.target.value })}
                  />
                </Grid>
              </>
            )}

            {/* Domain — how this risk code is organised in the catalog */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Domain
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={domains.some(d => d.code === formData.category) ? formData.category : ''}
                  displayEmpty
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <MenuItem value=""><em>Unassigned</em></MenuItem>
                  {domains.map((d) => (
                    <MenuItem key={d.code} value={d.code}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.category && !domains.some(d => d.code === formData.category) && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  Current value &ldquo;{formData.category}&rdquo; is not a known domain. Select one above to update.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.code || !formData.name}
          >
            {saving ? 'Saving...' : editingRisk ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RiskCodes
